import type {ClientTool, ClientToolCall} from '@github-ui/ahp-relay/relay-connection-runtime'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import type {ToolCallResult, ToolResultContentType} from '@microsoft/agent-host-protocol'

export const CREATE_REPOSITORY_TOOL_NAME = 'create_repository'

interface CreateRepositoryInput {
  name: string
  owner: string
  description?: string
  visibility: 'private' | 'public'
  initializeWithReadme: boolean
}

const CREATE_REPOSITORY_TOOL_DEFINITION: ClientTool['definition'] = {
  name: CREATE_REPOSITORY_TOOL_NAME,
  title: 'Create a repository',
  description:
    'Create a new GitHub repository as the signed-in user. Use this instead of `gh repo create` or the ' +
    'REST API, which cannot create repositories from this sandbox. The repository is private unless the user ' +
    'asks for a public one. Before calling, ask the user whether they want an empty repository or one ' +
    'initialized with a README on the default branch (usually `main`), unless they already said.',
  inputSchema: {
    type: 'object',
    properties: {
      name: {type: 'string', description: 'Repository name, without the owner.'},
      owner: {
        type: 'string',
        description: 'User or organization login to create the repository in. Defaults to the signed-in user.',
      },
      description: {type: 'string', description: 'Optional short repository description.'},
      visibility: {type: 'string', enum: ['private', 'public'], description: 'Defaults to private.'},
      initialize_with_readme: {
        type: 'boolean',
        description: 'True creates the default branch with a README; false creates an empty repository. Ask the user.',
      },
    },
    required: ['name', 'initialize_with_readme'],
  },
  annotations: {readOnlyHint: false, destructiveHint: false, idempotentHint: false, openWorldHint: true},
}

function parseInput(toolInput: string | undefined): CreateRepositoryInput {
  const input: unknown = JSON.parse(toolInput ?? '{}')
  if (!input || typeof input !== 'object') throw new Error('Provide a repository name.')
  const {name, owner, description, visibility, initialize_with_readme} = input as Record<string, unknown>
  const resolvedOwner = owner ?? getCurrentUserLogin()
  if (
    typeof name !== 'string' ||
    !/^[a-zA-Z0-9_.-]{1,100}$/.test(name) ||
    name === '.' ||
    name === '..' ||
    typeof resolvedOwner !== 'string' ||
    !/^[a-zA-Z0-9_-]{1,39}$/.test(resolvedOwner) ||
    (description !== undefined && (typeof description !== 'string' || description.length > 350)) ||
    (visibility !== undefined && visibility !== 'private' && visibility !== 'public') ||
    typeof initialize_with_readme !== 'boolean'
  ) {
    throw new Error(
      'Provide a valid repository name (letters, digits, ".", "-", "_"), an optional owner login, an optional ' +
        'description, visibility "private" or "public", and initialize_with_readme true or false.',
    )
  }
  return {
    name,
    owner: resolvedOwner,
    visibility: visibility ?? 'private',
    initializeWithReadme: initialize_with_readme,
    ...(description ? {description} : {}),
  }
}

function createdNwo(body: unknown, owner: string): string | undefined {
  const redirect = (body as {data?: {redirect?: unknown}} | null)?.data?.redirect
  if (typeof redirect !== 'string') return undefined
  let path: string
  try {
    const parsed = new URL(redirect, window.location.origin)
    if (parsed.origin !== window.location.origin) return undefined
    path = parsed.pathname
  } catch {
    return undefined
  }
  const match = /^\/([a-zA-Z0-9_-]{1,39})\/([a-zA-Z0-9_.-]{1,100})\/?$/.exec(path)
  const [, matchedOwner, matchedName] = match ?? []
  if (!matchedOwner || !matchedName || matchedOwner.toLowerCase() !== owner.toLowerCase()) return undefined
  return `${matchedOwner}/${matchedName}`
}

function failure(message: string): ToolCallResult {
  return {
    success: false,
    pastTenseMessage: 'Could not create a repository',
    error: {message},
    content: [{type: 'text' as ToolResultContentType.Text, text: message}],
  }
}

async function fulfil(call: ClientToolCall): Promise<ToolCallResult> {
  let input: CreateRepositoryInput
  try {
    input = parseInput(call.toolInput)
  } catch (error) {
    return failure(error instanceof Error ? error.message : 'Provide a repository name.')
  }
  try {
    const response = await reactFetchJSON('/repositories', {
      method: 'POST',
      body: {
        owner: input.owner,
        repository: {
          name: input.name,
          visibility: input.visibility,
          ...(input.initializeWithReadme ? {auto_init: '1'} : {}),
          ...(input.description ? {description: input.description} : {}),
        },
      },
    })
    // Do not echo the response's error text back to the agent: it is upstream content, not ours.
    if (!response.ok) {
      return failure(
        response.status === 422
          ? `Could not create ${input.owner}/${input.name}: the name may already exist, or you cannot create ` +
              `${input.visibility} repositories in ${input.owner}.`
          : `Could not create ${input.owner}/${input.name} (HTTP ${response.status}).`,
      )
    }
    // dotcom may normalize the name (e.g. strip `.git`), so trust its redirect, not our input.
    let body: unknown = null
    try {
      body = response.redirected ? null : await response.json()
    } catch {
      body = null
    }
    const nwo = createdNwo(body, input.owner)
    if (!nwo) {
      return failure(
        `Could not confirm that ${input.owner}/${input.name} was created. Check whether it exists before retrying.`,
      )
    }
    const url = `${window.location.origin}/${nwo}`
    return {
      success: true,
      pastTenseMessage: `Created repository ${nwo}`,
      structuredContent: {nwo, url, visibility: input.visibility},
      content: [{type: 'text' as ToolResultContentType.Text, text: `Created ${input.visibility} repository ${url}`}],
    }
  } catch {
    return failure(`Could not create ${input.owner}/${input.name}. Check whether it exists before trying again.`)
  }
}

/** Creates a repository as the signed-in user, for repoless Chat sessions whose sandbox token cannot. */
export function buildCreateRepositoryClientTool(): ClientTool {
  return {definition: CREATE_REPOSITORY_TOOL_DEFINITION, fulfil}
}

/** Host tools for a repository-less cloud session: baseline plus `create_repository` when enabled. */
export function repositorylessHostTools():
  | {kind: 'baseline-at-create'}
  | {kind: 'host-bound'; tools: readonly ClientTool[]} {
  return isFeatureEnabled('copilot_mission_control_create_repository_client_tool')
    ? {kind: 'host-bound', tools: [buildCreateRepositoryClientTool()]}
    : {kind: 'baseline-at-create'}
}
